import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isAdmin } from '../utils/adminCheck';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProtectedRoute({ children, adminOnly = false }) {
  const [status, setStatus] = useState('loading');
  const [userEmail, setUserEmail] = useState(null);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        // Check Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          if (mounted) {
            setStatus('unauthenticated');
          }
          return;
        }

        const email = session.user.email;
        setUserEmail(email);
        
        // Check if user is admin
        const adminStatus = isAdmin(email);
        setUserIsAdmin(adminStatus);

        // Sync with backend
        try {
          await fetch(`${BACKEND_URL}/api/auth/supabase-sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              supabase_user_id: session.user.id,
              email: email,
              full_name: session.user.user_metadata?.full_name || email?.split('@')[0],
            }),
          });
        } catch (syncError) {
          console.error('Backend sync error:', syncError);
        }

        // Admin users bypass subscription check
        if (adminStatus) {
          if (mounted) {
            setStatus('authenticated');
          }
          return;
        }

        // Non-admin users: check subscription status
        try {
          const subRes = await fetch(`${BACKEND_URL}/api/subscription/status`, {
            credentials: 'include',
          });
          
          if (subRes.ok) {
            const subData = await subRes.json();
            if (mounted) {
              setHasSubscription(subData.subscriptionActive === true);
              if (subData.subscriptionActive) {
                setStatus('authenticated');
              } else {
                setStatus('needs-subscription');
              }
            }
          } else {
            // Can't verify subscription - require it
            if (mounted) {
              setStatus('needs-subscription');
            }
          }
        } catch (subError) {
          console.error('Subscription check error:', subError);
          if (mounted) {
            setStatus('needs-subscription');
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (mounted) {
          setStatus('unauthenticated');
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setStatus('unauthenticated');
      } else if (event === 'SIGNED_IN') {
        checkAuth();
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFD]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9E00] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    localStorage.setItem('redirectAfterAuth', location.pathname);
    return <Navigate to="/auth" replace />;
  }

  if (status === 'needs-subscription') {
    return <Navigate to="/pricing" replace />;
  }

  if (adminOnly && !userIsAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
